import { memo, useCallback, useState } from 'react'
import { type Task, type Assignment, type Employee } from '../../lib/api'
import { GripVertical } from 'lucide-react'
import { AssigneeAvatars } from '../assignment/AssigneeControl'
import { ClientLabel } from './TaskControls'
import { useUpdateTask } from '../../hooks/useTaskMutations'

const COLUMNS = [
  { id: 'todo', label: 'To Do', color: 'var(--text-secondary)' },
  { id: 'in_progress', label: 'In Progress', color: 'var(--accent-primary)' },
  { id: 'review', label: 'Review', color: 'var(--state-warning)' },
  { id: 'done', label: 'Done', color: 'var(--state-success)' },
  { id: 'blocked', label: 'Blocked', color: 'var(--state-danger)' },
] as const

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, med: 2, low: 3 }

interface TaskBoardProps {
  tasks: Task[]
  assignmentsByTask?: Map<string, Assignment[]>
  employees?: Employee[]
  /** May move cards between columns. `roleCanWrite` — read_only is locked out. */
  canWrite?: boolean
  /** Whether client names link through to the client record. */
  canOpenClients?: boolean
  onTaskClick?: (taskId: string) => void
}

export function TaskBoard({
  tasks,
  assignmentsByTask,
  employees = [],
  canWrite = true,
  canOpenClients = false,
  onTaskClick,
}: TaskBoardProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null)
  // Optimistic — the card lands in the new column on drop and the write is
  // reconciled behind it, instead of the board freezing on a round-trip.
  const move = useUpdateTask()

  const handleDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('text/plain', taskId)
    e.dataTransfer.effectAllowed = 'move'
    setDraggedId(taskId)
  }, [])

  const handleDragEnd = useCallback(() => setDraggedId(null), [])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, columnId: string) => {
    e.preventDefault()
    const taskId = e.dataTransfer.getData('text/plain')
    setDraggedId(null)
    if (!taskId || !canWrite) return

    const task = tasks.find((t) => t.id === taskId)
    if (task && task.status !== columnId) {
      move.mutate({ id: taskId, patch: { status: columnId } })
    }
  }

  return (
    <div className="board">
      {COLUMNS.map((col) => {
        const columnTasks = tasks
          .filter((t) => t.status === col.id)
          .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))

        return (
          <div
            key={col.id}
            className="board-column"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            <div className="board-column-header">
              <span className="board-column-dot" style={{ background: col.color }} />
              <span className="board-column-title">{col.label}</span>
              <span className="board-column-count mono">{columnTasks.length}</span>
            </div>
            <div className="board-column-body">
              {columnTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  assignments={assignmentsByTask?.get(task.id) ?? []}
                  employees={employees}
                  draggable={canWrite}
                  canOpenClients={canOpenClients}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  isDragging={draggedId === task.id}
                  onClick={onTaskClick}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Memoised, with only stable props: dropping one card re-renders that card and
 * its two columns, not all hundred cards on the board. The handlers take the
 * task id rather than closing over it so their identity survives the parent's
 * re-render.
 */
const TaskCard = memo(function TaskCard({
  task,
  assignments,
  employees,
  draggable,
  canOpenClients,
  onDragStart,
  onDragEnd,
  isDragging,
  onClick,
}: {
  task: Task
  assignments: Assignment[]
  employees: Employee[]
  draggable: boolean
  canOpenClients: boolean
  onDragStart: (e: React.DragEvent, taskId: string) => void
  onDragEnd: () => void
  isDragging: boolean
  onClick?: (taskId: string) => void
}) {
  return (
    // Clickable anywhere for the mouse — that's the kanban expectation — but the
    // card itself is not `role="button"`: it contains a client link, and nesting
    // one interactive control inside another is invalid and confuses assistive
    // tech. The title below is the real control, so keyboard users get exactly
    // one focus stop per card.
    <div
      className={`board-card${isDragging ? ' board-card--dragging' : ''}`}
      draggable={draggable}
      onDragStart={(e) => onDragStart(e, task.id)}
      onDragEnd={onDragEnd}
      onClick={() => onClick?.(task.id)}
    >
      <div className="board-card-header">
        <GripVertical size={14} className="text-muted" />
        <span className={`priority-dot priority-${task.priority}`} />
      </div>
      {/* The client name sits above the title: on a board scanned at a glance,
          whose work it is reads before what the work is. */}
      <ClientLabel
        clientId={task.clientId}
        clientName={task.clientName}
        linkTo={canOpenClients}
        className="board-card-client"
      />
      <p
        className="board-card-title"
        title={task.title}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onClick?.(task.id) }}
      >
        {task.title}
      </p>
      <div className="board-card-meta">
        <span className="chip chip-sm">{task.discipline}</span>
        {task.due && <span className="mono text-muted text-xs">{task.due}</span>}
        <AssigneeAvatars assignments={assignments} employees={employees} />
      </div>
    </div>
  )
})
